/**
 * academy-ui/src/template/sections/canvas/canvasReaderModel.js
 *
 * PURE parse/validate of the persisted `section.canvas` payload produced by the
 * Canva-style Editor's persistence fold. This is the reader-side mirror of the editor's
 * `deserializeItem`: it turns the self-describing `section.canvas` block
 * (`{ referenceWidth, theme, items[] }`) into a validated, render-ready model that the
 * `CanvasSectionReader` consumes so the published page reproduces the editor's geometry,
 * ordering, and theme.
 *
 * NO React, NO DOM. It reuses the reader's existing geometry contract from
 * `layoutCore.js` (`validateElementLayout` for the `{ x, y, width, height, z }` shape with
 * optional `backgroundColor`/`textColor`/`hidden`, and `validateReferenceWidth` for the
 * reference width range), mirroring the incomplete/invalid partitioning of the legacy
 * `layoutResolver` by SKIPPING malformed items rather than throwing.
 *
 * ZERO-LEGACY / ISOLATION CONSTRAINT: the reader cannot reach into the console app, so
 * this module imports NOTHING from `admin-console-ui`. It depends only on the pure
 * geometry helpers in `../../layout/layoutCore`.
 *
 * Defensive contract: every export tolerates missing/odd input (no `canvas`, no `items`,
 * non-object shapes) and NEVER throws. Item ORDER is preserved. When a section has no
 * valid canvas payload (missing block, or a reference width that fails validation),
 * `parseCanvasSection` returns `null` so the caller can fall back to another renderer.
 *
 * Requirements: 14.3 (Reader displays each Item at the persisted position/size).
 * Design: sections/canvas/canvasReaderModel.js; satisfies the PARSING half of Property 25
 * (reader reproduces persisted geometry, order, and theme).
 */

import { validateElementLayout, validateReferenceWidth } from '../../layout/layoutCore';
import { CANVAS_SCHEMA_VERSION } from 'canvas-engine/item-contract/itemConstants';

// ── Internal helpers ─────────────────────────────────────────────────────────

/** True for a non-null, non-array plain object. */
function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Resolve the effective schema version for a Canvas_Payload.
 *
 * Backward-compat / versioning contract (Req 17.2, 17.4):
 *   - an absent or non-positive-integer `schemaVersion` is treated as version 1
 *     (the earliest supported version);
 *   - a version greater than the reader's supported `CANVAS_SCHEMA_VERSION` is
 *     clamped down to the supported version and flagged via `warning: true` so the
 *     caller can surface that the payload uses a newer, unsupported schema.
 *
 * Never throws; never mutates input.
 *
 * @param {*} rawVersion - the stored `canvas.schemaVersion`
 * @returns {{ schemaVersion: number, warning: boolean }}
 */
function resolveSchemaVersion(rawVersion) {
  const isPositiveInteger = typeof rawVersion === 'number'
    && Number.isInteger(rawVersion)
    && rawVersion > 0;

  if (!isPositiveInteger) {
    return { schemaVersion: 1, warning: false };
  }
  if (rawVersion > CANVAS_SCHEMA_VERSION) {
    return { schemaVersion: CANVAS_SCHEMA_VERSION, warning: true };
  }
  return { schemaVersion: rawVersion, warning: false };
}

/** True for a non-empty string (used to require a usable item `id`). */
function isNonEmptyString(value) {
  return typeof value === 'string' && value.length > 0;
}

/**
 * Extract the persisted `canvas` block from a section envelope, if present and shaped
 * like an object. Returns `null` for any non-conforming input (never throws).
 *
 * @param {*} section
 * @returns {object|null}
 */
function getCanvasBlock(section) {
  if (!isPlainObject(section)) return null;
  const { canvas } = section;
  return isPlainObject(canvas) ? canvas : null;
}

/**
 * Validate and normalize a single persisted item record. Returns the render-ready item
 * `{ id, type, geometry, content, style }` when its geometry passes
 * `validateElementLayout`, or `null` when the record is malformed (non-object or invalid
 * geometry) so the caller can SKIP it — mirroring the resolver's incomplete/invalid
 * partitioning. Never throws.
 *
 * @param {*} raw - one entry of `canvas.items[]`
 * @returns {{ id: *, type: *, geometry: object, content: object, style: object }|null}
 */
function parseItem(raw) {
  if (!isPlainObject(raw)) return null;

  // Require a non-empty string `id` per the serialization contract (Req 1.2, 15.2);
  // a missing or non-string/empty id makes the record unaddressable, so drop it.
  if (!isNonEmptyString(raw.id)) return null;

  const geometryResult = validateElementLayout(raw.geometry);
  if (!geometryResult.ok) return null;

  return {
    id: raw.id,
    type: raw.type,
    geometry: geometryResult.value,
    content: isPlainObject(raw.content) ? { ...raw.content } : {},
    style: isPlainObject(raw.style) ? { ...raw.style } : {},
  };
}

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Parse the persisted `section.canvas` payload into a validated reader model.
 *
 * Returns `null` when the section carries no usable canvas payload (so the section
 * dispatcher can fall back to a non-canvas renderer — Req 13.3, 13.4, 14.3):
 *   - the section is not an object, or has no `canvas` block;
 *   - the canvas reference width fails `validateReferenceWidth`.
 *
 * Otherwise returns `{ schemaVersion, referenceWidth, theme, items, schemaWarning }`:
 *   - `schemaVersion` is the resolved schema version: an absent/non-positive-integer
 *     stored version is treated as 1, and a version newer than the reader's supported
 *     `CANVAS_SCHEMA_VERSION` is clamped to the supported version (Req 17.2, 17.4);
 *   - `referenceWidth` is the validated integer reference width;
 *   - `theme` is the sparse `Section_Theme` object (or `{}` when absent/odd — Req 10.4);
 *   - `items` is the ordered array of validated items, with malformed items SKIPPED
 *     (non-object, missing/empty `id`, or invalid `geometry`) preserving the order of
 *     the remaining items (Req 2.3, 15.2); each kept item is
 *     `{ id, type, geometry, content, style }`;
 *   - `schemaWarning` is `true` only when the stored version exceeded the supported
 *     version and was clamped (Req 17.4); otherwise `false`.
 *
 * Defensive: never throws, never mutates the input section/payload (the returned model
 * is built from fresh copies — Req 16.1, 16.2, 16.4), tolerates missing `items`, and
 * preserves item order.
 *
 * @param {*} section - an old-shaped section envelope (may carry a `canvas` block)
 * @returns {{ schemaVersion: number, referenceWidth: number, theme: object, items: Array, schemaWarning: boolean }|null}
 */
export function parseCanvasSection(section) {
  const canvas = getCanvasBlock(section);
  if (canvas === null) return null;

  const rw = validateReferenceWidth(canvas.referenceWidth);
  if (!rw.ok) return null;

  const { schemaVersion, warning } = resolveSchemaVersion(canvas.schemaVersion);

  const theme = isPlainObject(canvas.theme) ? { ...canvas.theme } : {};

  const rawItems = Array.isArray(canvas.items) ? canvas.items : [];
  const items = [];
  for (let i = 0; i < rawItems.length; i += 1) {
    const item = parseItem(rawItems[i]);
    if (item !== null) {
      items.push(item);
    }
  }

  return {
    schemaVersion,
    referenceWidth: rw.value,
    theme,
    items,
    schemaWarning: warning,
  };
}

/**
 * True when `section` carries a parseable canvas payload (i.e. `parseCanvasSection`
 * would return a model rather than `null`). Never throws.
 *
 * @param {*} section
 * @returns {boolean}
 */
export function isValidCanvasSection(section) {
  return parseCanvasSection(section) !== null;
}

export default parseCanvasSection;
